/**
 * Payload CMS relationship field value.
 */
export type Relationship<T> = T | number | string | null | undefined;

/**
 * The supported id types for relationships.
 */
export type RelationshipId = string | number;

/**
 * Type guard for populated relationship-like objects that contain an "id"
 * of type "string" or "number".
 */
function hasRelationshipId(value: unknown): value is { id: RelationshipId } {
	if (value === null || typeof value !== "object") return false;
	if (!Object.hasOwn(value, "id")) return false;

	const id = (value as { id?: unknown }).id;
	return typeof id === "string" || typeof id === "number";
}

interface ResolveRelationshipIdOptions {
	/** Value returned when no id can be resolved. */
	fallback?: string;
	/** When false, returns `undefined` instead of the fallback value. */
	enableFallback?: boolean;
}

/**
 * Resolves a Payload relationship value to its id.
 *
 * Accepts either a raw id or a populated relationship object. If the value
 * does not contain an id, the function returns `fallback` when
 * `enableFallback` is enabled; otherwise it returns `undefined`.
 *
 * @param value - Relationship value from Payload (raw id or populated object).
 * @param options - Optional resolution settings.
 * @returns The resolved relationship id, the fallback value, or `undefined`.
 */
export function resolveRelationshipId<T>(value: Relationship<T>, options?: ResolveRelationshipIdOptions & { enableFallback?: true }): string | number;
export function resolveRelationshipId<T>(
	value: Relationship<T>,
	options: ResolveRelationshipIdOptions & { enableFallback: false },
): string | number | undefined;
export function resolveRelationshipId<T>(value: Relationship<T>, options: ResolveRelationshipIdOptions = {}) {
	const { enableFallback = true, fallback = "default" } = options;

	if (typeof value === "string" || typeof value === "number") return value;
	if (hasRelationshipId(value)) return value.id;

	return enableFallback ? fallback : undefined;
}

/**
 * Normalizes a Payload relationship field to its populated object form.
 *
 * @param value - Relationship value from Payload (raw id, populated object,
 *   null, or undefined).
 * @returns The populated relationship object, or "undefined" if the value is not
 *   populated.
 */
export function getRelationshipObject<T>(value: Relationship<T>): T | undefined {
	if (typeof value === "undefined" || typeof value === "string" || typeof value === "number" || value === null) return undefined;
	return value;
}

/**
 * Filters a Payload relationship array to only its populated object entries.
 *
 * @param value - Array of relationship values from Payload (raw ids or populated objects),
 *   null, or undefined.
 * @returns An array containing only the populated relationship objects.
 */
export function getRelationshipArray<T>(value: Relationship<T>[] | null | undefined): T[] {
	return (value ?? []).filter((item): item is T => typeof item !== "string" && typeof item !== "number");
}

/**
 * Generates the typography variant CSS class (ms-font-{id}) from a Payload typography relationship.
 *
 * @param value - Typography variant relationship (id or populated { id }).
 * @returns The class name, or undefined if no variant is set.
 */
export function typographyVariantClass(value: string | number | { id: string | number } | null | undefined): string | undefined {
	const id = resolveRelationshipId(value, { enableFallback: false });
	return id != null ? `ms-font-${id}` : undefined;
}

/**
 * Generates a background color CSS class name from a theme color ID.
 *
 * @param themeColorId - The ID of the theme color.
 * @returns The background color class name, or null if no ID is provided.
 * @see https://developer.marketsuite.nl/cms/api-reference/theme-configuration-class#background-color-class
 */
export const backgroundColorClass = (themeColorId: string | undefined | null) => {
	if (!themeColorId) return null;

	return "ms-bg-" + themeColorId;
};

/**
 * Generates a spacing CSS class name for margins or paddings.
 *
 * @param blockSpacingId - The ID of the block spacing setting.
 * @param type - Whether to generate a margin or padding class.
 * @returns The generated spacing class name.
 * @see https://developer.marketsuite.nl/cms/api-reference/theme-configuration-class#block-spacing-class
 */
export const blockSpacingClass = (blockSpacingId: string | undefined | null, type: "margin" | "padding") => {
	if (!blockSpacingId) return `ms-block-${type}-default`;

	return `ms-block-${type}-${blockSpacingId}`;
};

/**
 * Generates a padding CSS class name for wraps.
 *
 * @param blockPaddingId - The ID of the block padding setting.
 * @returns The generated wrap class name.
 * @see https://developer.marketsuite.nl/cms/api-reference/theme-configuration-class#block-padding-class
 */
export const blockPaddingClass = (blockPaddingId: string | undefined | null) => {
	if (!blockPaddingId) return "ms-wrap-padding-default";

	return `ms-wrap-padding-${blockPaddingId}`;
};

/**
 * Generates a text alignment CSS class name for horizontal alignment.
 *
 * @param alignment - The horizontal alignment value.
 * @returns The text alignment class name.
 * @see https://developer.marketsuite.nl/cms/api-reference/theme-configuration-class#horizontal-alignment-class
 */
export const horizontalAlignmentClass = (alignment: "start" | "center" | "end" | null | undefined) => {
	const hAlign: Record<string, string> = {
		start: "text-left justify-start",
		center: "text-center justify-center",
		end: "text-right justify-end",
	};

	return hAlign[alignment ?? "start"];
};

/**
 * Generates a text alignment CSS class name for vertical alignment.
 *
 * @param alignment - The vertical alignment value.
 * @param type - Whether to generate `items-*` (default) or `justify-*` classes.
 * @returns The text alignment class name.
 * @see https://developer.marketsuite.nl/cms/api-reference/theme-configuration-class#vertical-alignment-class
 */
export const verticalAlignmentClass = (alignment: "start" | "center" | "end" | null | undefined, type: "justify" | "align" = "align") => {
	const vAlign: Record<string, string> = {
		start: type === "justify" ? "justify-start" : "items-start",
		center: type === "justify" ? "justify-center" : "items-center",
		end: type === "justify" ? "justify-end" : "items-end",
	};

	return vAlign[alignment ?? "start"];
};

/**
 * Generates a border shape CSS class name from a theme border shape ID.
 *
 * @param borderShapeId - The ID of the theme border shape.
 * @returns The border shape class name. When no ID is provided, returns the default class.
 * @see https://developer.marketsuite.nl/cms/api-reference/theme-configuration-class#border-shape-class
 */
export const borderShapeClass = (borderShapeId: string | undefined | null) => {
	if (!borderShapeId) return "ms-border-shape-default";

	return `ms-border-shape-${borderShapeId}`;
};
